<template>
  <DefaultPage>
    <FilterMain
      v-model="filterQuery"
      @input="debouncedSearch"
      @clear="clearInputSearch"
    />

    <div class="applications-view-wrapper">
      <div v-if="isLoadingApps" class="loading-wrapper">
        <BaseLoadingSpinner :with-overlay="false" :size="60" />
      </div>
      <template v-else>
        <section class="applications-wrapper">
          <OvApplicationsLane
            title="Nekade"
            status="rejected"
            :applications="applicationsRejected"
            :to-path="toPath"
          ></OvApplicationsLane>

          <OvApplicationsLane
            title="Avslutade"
            status="closed"
            :applications="applicationsClosed"
            :to-path="toPath"
          ></OvApplicationsLane>

          <OvApplicationsLane
            title="Borttagna"
            status="removed"
            :applications="applicationsDeleted"
            :to-path="toPath"
          ></OvApplicationsLane>
        </section>
      </template>
      <router-view />
    </div>
  </DefaultPage>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import debounce from 'lodash.debounce';
import { APP_NAMES, APP_PATHS } from '@/utils/constants';

import statusTypes from '@/utils/statusTypes';
import OvApplicationsLane from '@/components/OvApplicationsLane.vue';
import FilterMain from '@/components/FilterMain.vue';
import BaseLoadingSpinner from '@/components/BaseLoadingSpinner.vue';
import DefaultPage from '@/components/DefaultPage.vue';

export default {
  name: 'OvArchive',
  components: {
    OvApplicationsLane,
    FilterMain,
    BaseLoadingSpinner,
    DefaultPage
  },
  beforeRouteUpdate(to) {
    this.maybeClearInputSearch(to.path);
  },
  beforeRouteLeave(to) {
    //Leaving to new route and unmounting
    this.maybeClearInputSearch(to.path);
  },
  data() {
    return {
      filterQuery: this.$store.state.applications.filter || ''
    };
  },
  computed: {
    ...mapGetters({
      filteredOvApps: 'applications/filteredOvApps'
    }),
    ...mapState('applications', ['isLoadingApps']),
    ...mapState({
      activeApp: state => state.app.activeApp
    }),
    toPath() {
      return this.activeApp === APP_NAMES.OVERLATELSER_COMPANY
        ? `/${APP_PATHS.OVERLATELSER_COMPANY}/arkiv`
        : `/${APP_PATHS.OVERLATELSER_PRIVATE}/arkiv`;
    },
    applicationsRejected() {
      if (!this.filteredOvApps) {
        return [];
      }
      return this.filteredOvApps.filter(
        x => x.landlordStatus === statusTypes.REJECTED
      );
    },
    applicationsClosed() {
      if (!this.filteredOvApps) {
        return [];
      }
      return this.filteredOvApps.filter(
        x => x.landlordStatus === statusTypes.CLOSED
      );
    },
    applicationsDeleted() {
      if (!this.filteredOvApps) {
        return [];
      }
      return this.filteredOvApps.filter(
        x =>
          x.landlordStatus === statusTypes.PENDING_REMOVE ||
          x.landlordStatus === statusTypes.REMOVED
      );
    }
  },

  async created() {
    this.debouncedSearch = debounce(this.search, 300);
    await this.fetchOvArchivedApps();
    // Ladda pending i bakgrunden så vi kan räkna matchningar i andra vyn
    this.fetchOvApps();
    const firstAppId = this.filteredOvApps?.[0]?.id;
    if (firstAppId) {
      await this.fetchLandlordUsersWithUnassigned(firstAppId);
    }
  },

  methods: {
    ...mapActions({
      fetchOvArchivedApps: 'applications/fetchOvArchivedApps',
      fetchOvApps: 'applications/fetchOvApps',
      fetchLandlordUsersWithUnassigned:
        'applications/fetchLandlordUsersWithUnassigned'
    }),
    ...mapMutations({
      setFilter: 'applications/setFilter'
    }),
    maybeClearInputSearch(path) {
      // Behåll sökningen inom överlåtelser-modulen (alla/arkiv)
      if (
        !path.includes(APP_PATHS.OVERLATELSER_PRIVATE) &&
        !path.includes(APP_PATHS.OVERLATELSER_COMPANY)
      ) {
        this.clearInputSearch();
      }
    },
    clearInputSearch() {
      this.filterQuery = '';
      this.debouncedSearch.cancel();
      this.search('');
    },
    search(query) {
      this.setFilter(query);
    }
  }
};
</script>

<style scoped>
.applications-wrapper {
  display: flex;
  flex-direction: row;
  height: 100%;
  max-width: 1080px;
}

.applications-view-wrapper {
  flex: 1;
  margin-left: 20px;
}

.loading-wrapper {
  position: fixed;
  z-index: 99;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: rgba(0, 0, 0, 0.6);
}
</style>
